import styled from 'styled-components';
import {
  QtyButton,
  PriceButton,
  ActionCell,
} from '#/components/rfq-common/ActionButtons';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import { RowIndexProvider } from '#/components/UnifiedRfqs/components/RfqRowBase';
import { BaseRfqAction as RfqAction } from '#/components/UnifiedRfqs/rfqs/action/BaseRfqAction';
import { RfqActionRow } from '#/components/UnifiedRfqs/rfqs/action/common';
import { portage5 } from '@paradigm/design-system/src/colors';
import { useUMPlatformKillDisabled } from '@paradigm/features/src/platform-state/contexts/providers';
import { useBboView } from '@paradigm/features/src/unified-rfqs/ui/bbo-view-context';

interface Props {
  productCode: string | undefined;
  isQtyNormalized: boolean;
}

export default function ScreenPricesAction({
  productCode,
  isQtyNormalized,
}: Props) {
  const isPlatformKillDisabled = useUMPlatformKillDisabled();
  const {
    bestBidQty,
    bestBidPrice,
    bestAskQty,
    bestAskPrice,
    bestBidIv,
    bestAskIv,
    isDated,
  } = useBboView();
  const bidQty = (
    <QtyButton
      disabled={isPlatformKillDisabled}
      quantity={bestBidQty.value ?? null}
      productCode={productCode}
      isNormalized={isQtyNormalized}
      isDated={isDated}
    />
  );

  const askQty = (
    <QtyButton
      disabled={isPlatformKillDisabled}
      quantity={bestAskQty.value ?? null}
      productCode={productCode}
      isNormalized={isQtyNormalized}
      isDated={isDated}
    />
  );

  const bidPrice = (
    <PriceButton disabled={isPlatformKillDisabled}>
      {bestBidPrice.formatted}
    </PriceButton>
  );

  const askPrice = (
    <PriceButton disabled={isPlatformKillDisabled}>
      {bestAskPrice.formatted}
    </PriceButton>
  );

  return (
    <RowIndexProvider value={3}>
      <ScreenRow>
        <ActionContainer>
          <Label>
            <Title>Screen Bid</Title>
            <SubTitle>Unhedged</SubTitle>
          </Label>
        </ActionContainer>
        <RfqAction
          bidQty={bidQty}
          bidPrice={bidPrice}
          askQty={askQty}
          askPrice={askPrice}
          bidIv={bestBidIv.formatted}
          askIv={bestAskIv.formatted}
        />
        <ActionContainer>
          <Label>
            <Title>Screen Ask</Title>
            <SubTitle>Unhedged</SubTitle>
          </Label>
        </ActionContainer>
      </ScreenRow>
    </RowIndexProvider>
  );
}

const ScreenRow = styled.div`
  height: auto;
  position: relative;
  display: grid;
  justify-content: center;
  align-items: center;
  width: 100%;
  grid-template-columns: 140px 438px 140px;
  grid-template-areas: 'qsell quotes qbuy';
  span,
  svg {
    margin: unset;
  }

  ${RfqActionRow} {
    background-color: ${portage5};
  }

  ${ActionCell} {
    color: rgba(255, 255, 255, 0.5);
  }

  ${PriceButton} {
    color: rgba(255, 255, 255, 0.5);
    background-color: rgba(0, 0, 0, 0.2);
    border-color: rgba(0, 0, 0, 0.2);
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const SubTitle = styled.span`
  opacity: 0.5;
  font-size: 10px;
`;

const Title = styled.span`
  width: 100%;
`;

const ActionContainer = styled.div`
  align-items: center;
  height: 100%;
  display: grid;
  padding: 0 6px;
  column-gap: 6px;
`;

const Label = styled.div`
  color: white;
  opacity: 0.5;
  font-size: 13px;
  font-weight: 400;
  line-height: 13px;
  text-align: center;
  display: grid;
`;
